/**
 * がんばりポイントとリーグ(docs/12_challenge_ranking.md §2)。
 *
 * docs/12 §2 は競争を2軸に分ける:
 *   **リーグ = どりょく(学習量)/ チャレンジ = じつりょく(スコア)**
 *
 * がんばりポイントは**タイピングでもパズルでも貯まる**。順位づけ・部屋割り・
 * 週明けの昇降格は**全部サーバー**(Cloud Functions)が持つので、
 * ここは「サーバーが組んだ順位表を受け取って描く」だけ。
 * クライアントはポイントの式も昇降格の判定も1つも持たない。
 *
 * ローカルモード(Firebase 未設定)には送信先が無いので `NoLeagueService` が
 * `supported: false` を返し、**画面はリーグのカードを丸ごと出さない**。
 *
 * ここは `factory.ts` から静的に読まれるので、重いものを import しないこと。
 */

/** リーグの段位(下から上へ)。 */
export type LeagueTier = 'bronze' | 'silver' | 'gold' | 'diamond' | 'master';

/** 順位表の1行。 */
export interface LeagueRow {
  readonly rank: number;
  readonly displayName: string;
  /** なかまキャラの絵文字(実ユーザーは `null`)。 */
  readonly emoji: string | null;
  /** その週のがんばりポイント。 */
  readonly xp: number;
  /** 自分の行か。 */
  readonly self: boolean;
  /**
   * **なかまキャラ**か(docs/12 §2)。
   * `true` の行には必ず「なかまキャラ」バッジを出す。
   * 実在のユーザーを装う隠し bot は禁止されているので、ここを隠してはいけない。
   */
  readonly isCharacter: boolean;
}

/** 前週の精算結果(週明けに1回だけ演出する)。 */
export interface LeagueSettlement {
  readonly weekId: string;
  readonly fromTier: LeagueTier;
  readonly toTier: LeagueTier;
  /** `absent` = 先週はおやすみだった(1段さがる)。 */
  readonly movement: 'promoted' | 'stayed' | 'demoted' | 'absent';
  readonly rank: number | null;
  readonly total: number;
  readonly xp: number;
  /** もらったトロフィーコイン。 */
  readonly trophyEarned: number;
}

/** getLeague の応答。 */
export interface LeagueView {
  readonly weekId: string;
  readonly startsAt: number;
  readonly endsAt: number;
  readonly tier: LeagueTier;
  /** `null` = まだ今週1ポイントも貯めていない(部屋にいない)。 */
  readonly roomId: string | null;
  readonly rows: readonly LeagueRow[];
  readonly selfRank: number | null;
  readonly selfXp: number;
  /** 上位何人が昇格するか(昇格ラインの表示に使う)。 */
  readonly promoteCount: number;
  /** 下位何人が降格するか(降格ラインの表示に使う)。 */
  readonly demoteCount: number;
  readonly lastWeek: LeagueSettlement | null;
}

/**
 * リーグの窓口。
 *
 * `supported: false` のときは**画面に導線を出さない**(ローカルモード)。
 * 通信できなかったら `null` を返す。**画面はそれで止まらない**。
 */
export interface LeagueService {
  readonly supported: boolean;
  /** 今週の自分の部屋と順位表。 */
  load(): Promise<LeagueView | null>;
}

/**
 * ローカルモード用の何もしない実装。
 *
 * リーグは**サーバーの部屋割りと集計が前提**なので、ローカルモードでは成立しない。
 * カードごと隠すのが正しい振る舞い(「あそべません」と出して期待させない)。
 */
export class NoLeagueService implements LeagueService {
  readonly supported = false;

  async load(): Promise<LeagueView | null> {
    return null;
  }
}

// ── 表示のことば(docs/12 §2「文言はやさしく」)────────────────────────

/** リーグの名前(ひらがな主体。CLAUDE.md ルール5)。 */
export const LEAGUE_LABELS: Readonly<Record<LeagueTier, string>> = {
  bronze: 'ブロンズ',
  silver: 'シルバー',
  gold: 'ゴールド',
  diamond: 'ダイヤ',
  master: 'マスター',
};

/** リーグのしるし。 */
export const LEAGUE_EMOJI: Readonly<Record<LeagueTier, string>> = {
  bronze: '🥉',
  silver: '🥈',
  gold: '🥇',
  diamond: '💎',
  master: '👑',
};
